import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../auth/AuthContext";
import { useActiveBusinessId } from "../auth/useActiveBusiness";
import {
  fetchLatest,
  markViewed as apiMarkViewed,
  markAllViewed as apiMarkAllViewed,
  routeForNotification,
} from "./api";

/* ------------------------------------------------------------------ *
 * Business notifications — badge count + "something new" polling.
 *
 * Delivery is pull only (no socket), so this polls
 * GET /notifications/latest?unviewed_only=true every POLL_MS while the tab
 * is visible, pauses while hidden, and re-polls as soon as it comes back
 * or the open business changes. The feed and badge belong to a business,
 * so everything here resets when the business id does.
 *
 * A new unviewed item (id differs from the last one we toasted for this
 * business) raises a toast that jumps to the item's subject. The last
 * toasted id is kept in sessionStorage per business so a reload does not
 * re-announce the same request.
 * ------------------------------------------------------------------ */

const POLL_MS = 45 * 1000;
const SEEN_KEY = (bizId) => `rf-notif-seen:${bizId}`;

/* `{ id, created_at }` of the newest item announced for a business. Viewing
   the newest item makes the poll return the next-newest UNVIEWED one, which
   is older, not new — so "new" means created after the last announced one,
   not merely a different id. */
const readSeen = (bizId) => {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY(bizId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const writeSeen = (bizId, item) => {
  try {
    sessionStorage.setItem(
      SEEN_KEY(bizId),
      JSON.stringify({ id: String(item._id), created_at: item.created_at || null })
    );
  } catch (_) {}
};
const isNewerThan = (item, seen) => {
  if (!seen) return true;
  if (String(item._id) === String(seen.id)) return false;
  if (item.created_at && seen.created_at) {
    return new Date(item.created_at).getTime() > new Date(seen.created_at).getTime();
  }
  return true;
};

const NotificationsContext = createContext(null);

export const useNotifications = () => useContext(NotificationsContext);

export function NotificationsProvider({ children }) {
  const auth = useAuth() || {};
  const user = auth.user;
  const businessId = useActiveBusinessId();
  const navigate = useNavigate();

  const [unviewedCount, setUnviewedCount] = useState(0);
  const [latest, setLatest] = useState(null);
  // Bumped whenever viewed state changes so open lists (bell dropdown,
  // feed page) know to refetch.
  const [version, setVersion] = useState(0);

  const bizRef = useRef(businessId);
  bizRef.current = businessId;
  const inFlight = useRef(false);
  // useNavigate() returns a new function on every location change; going
  // through a ref keeps `announce`/`poll` stable so the polling effect is
  // not torn down (and the badge zeroed) on every route change.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const announce = useCallback(
    (item) => {
      const to = routeForNotification(item);
      toast.info(
        <div className="nf-toast">
          <b>{item.title || "New notification"}</b>
          {item.message && <span>{item.message}</span>}
        </div>,
        {
          toastId: `nf-${item._id}`,
          autoClose: 8000,
          onClick: () => {
            apiMarkViewed(item._id)
              .then((d) => {
                if (d && typeof d.unviewed_count === "number") {
                  setUnviewedCount(d.unviewed_count);
                }
                setVersion((v) => v + 1);
              })
              .catch(() => {});
            if (to) navigateRef.current(to, { state: { notification: item } });
          },
        }
      );
    },
    []
  );

  /* One poll: badge count + toast for a not-yet-announced unviewed item. */
  const userRef = useRef(user);
  userRef.current = user;
  /* `force` ignores the hidden-tab pause: the first fetch after sign-in
     or a business switch must land so the badge is right when the tab is
     next looked at; only the interval ticks pause while hidden. */
  const poll = useCallback(async (force = false) => {
    const bizId = bizRef.current;
    if (!userRef.current || !bizId || inFlight.current) return;
    if (
      force !== true &&
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      return;
    }
    inFlight.current = true;
    try {
      const data = await fetchLatest(true);
      // The business may have changed while the request was out.
      if (bizRef.current !== bizId) return;
      const count = Number(data.unviewed_count) || 0;
      setUnviewedCount(count);
      const item = data.item || null;
      setLatest(item);
      if (item && item._id && isNewerThan(item, readSeen(bizId))) {
        writeSeen(bizId, item);
        announce(item);
      }
    } catch (_) {
      // Network blip or a 401 mid-refresh — the next tick tries again.
    } finally {
      inFlight.current = false;
    }
  }, [announce]);

  // Reset + immediate poll on sign-in and on business change; then tick.
  useEffect(() => {
    setUnviewedCount(0);
    setLatest(null);
    if (!user || !businessId) return undefined;
    poll(true);
    const id = setInterval(() => poll(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user, businessId, poll]);

  /* Mark one viewed and take the badge from the server's answer. Resolves
     with the route the notification points at (or null). */
  const markViewed = useCallback(async (item) => {
    const id = typeof item === "string" ? item : item && item._id;
    if (!id) return null;
    if (typeof item === "object" && item.created_at && bizRef.current) {
      const seen = readSeen(bizRef.current);
      if (isNewerThan(item, seen)) writeSeen(bizRef.current, item);
    }
    try {
      const data = await apiMarkViewed(id);
      if (data && typeof data.unviewed_count === "number") {
        setUnviewedCount(data.unviewed_count);
      }
    } catch (_) {
      /* the item stays unviewed; the next list fetch shows the truth */
    }
    setVersion((v) => v + 1);
    return typeof item === "string" ? null : routeForNotification(item);
  }, []);

  const markAll = useCallback(async () => {
    try {
      const data = await apiMarkAllViewed();
      if (data && typeof data.unviewed_count === "number") {
        setUnviewedCount(data.unviewed_count);
      }
    } catch (_) {}
    setVersion((v) => v + 1);
  }, []);

  /* For screens that change viewed state themselves (the feed page's
     "put back" action) — accept the server's count and tell lists. */
  const applyCount = useCallback((count) => {
    if (typeof count === "number") setUnviewedCount(count);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(
    () => ({
      businessId,
      unviewedCount,
      latest,
      version,
      refresh: poll,
      markViewed,
      markAll,
      applyCount,
      // Badge only — for reads that happen to return the count.
      setCount: setUnviewedCount,
    }),
    [businessId, unviewedCount, latest, version, poll, markViewed, markAll, applyCount]
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}
